import { mstCommon, segmentMst } from '@libs/domain';
import { mstListSearchFieldConfig, mstRegistrationFieldConfig } from '@libs/templates/mst';
import { Characters, Item, ItemName, Locale, WithExclusion } from '@ps-smart-cp/ppfw-client';
import type { SegmentMstSettingProvider, SingleSegmentMst, SingleSegmentMstRange } from '../models';

function getMetaSegment(segmentMstKey: number, metaSegments: SegmentMstSettingProvider[]) {
  return metaSegments.find(({ key }) => key === segmentMstKey)!;
}

function getHigherRankMetaSegment(segmentMstKey: number, metaSegments: SegmentMstSettingProvider[]) {
  const thisSegment = getMetaSegment(segmentMstKey, metaSegments);
  return metaSegments.find(({ key }) => thisSegment.higherRankSegmentMstKey === key);
}

function createSegmentMstItemName(
  segmentMstKey: number,
  metaSegments: SegmentMstSettingProvider[],
  prefix = '',
) {
  return ItemName.of([Locale.ja, getMetaSegment(segmentMstKey, metaSegments).displayName + prefix]);
}

function createSegmentMstItemAttribute(metaSegment?: SegmentMstSettingProvider, required = false) {
  return Characters.preset.halfWidth(
    metaSegment ? { minLen: metaSegment.length, maxLen: metaSegment.length, required } : {},
  );
}

/**
 * 登録用のitemを作成する
 */
function createSegmentMstItem(segmentMstKey: number, metaSegments: SegmentMstSettingProvider[]) {
  return {
    code: Item.of(
      mstCommon.names.code,
      createSegmentMstItemAttribute(getMetaSegment(segmentMstKey, metaSegments), true),
    ),
  };
}

/**
 * 検索/登録用のitemを作成する
 */
function createSegmentMstCodeItem(
  segmentMstKey: number,
  metaSegments: SegmentMstSettingProvider[],
  required = false,
) {
  return {
    segmentMstCode: Item.of(
      createSegmentMstItemName(segmentMstKey, metaSegments),
      createSegmentMstItemAttribute(getMetaSegment(segmentMstKey, metaSegments), required),
    ),
  };
}

/**
 * 範囲検索用のitemを作成する
 */
function createSegmentMstCodeRangeItem(
  segmentMstKey: number,
  metaSegments: SegmentMstSettingProvider[],
  suffix: string,
) {
  return {
    segmentMstCodeRange: Item.of(
      createSegmentMstItemName(segmentMstKey, metaSegments, suffix),
      WithExclusion.of(Characters.preset.alphanumericalsRange({ maxLen: 250 })),
    ),
  };
}

function createHigherSegmentMstItem(segmentMstKey: number, metaSegments: SegmentMstSettingProvider[]) {
  return {
    higherRankSegmentCode: Item.of(
      segmentMst.names.higherRankSegmentCode,
      createSegmentMstItemAttribute(getHigherRankMetaSegment(segmentMstKey, metaSegments)),
    ),
  };
}

function createSegmentMstListSearchFieldConfig(
  segmentMstKey: number,
  metaSegments: SegmentMstSettingProvider[],
) {
  return {
    ...mstListSearchFieldConfig,
    ...(segmentMstKey > 1 ? createHigherSegmentMstItem(segmentMstKey, metaSegments) : null),
  };
}

function createSegmentMstRegistrationFieldConfig(
  segmentMstKey: number,
  metaSegments: SegmentMstSettingProvider[],
) {
  return {
    ...mstRegistrationFieldConfig,
    ...createSegmentMstItem(segmentMstKey, metaSegments),
    ...(segmentMstKey > 1 ? createHigherSegmentMstItem(segmentMstKey, metaSegments) : null),
  };
}

function createSegmentCodeRangeBodies(segmentCodeRanges: SingleSegmentMstRange[]) {
  return Object.fromEntries(
    segmentCodeRanges.map((curr) => [curr.key.toString(), curr.segmentMstCodeRange.body]),
  );
}

function createSegmentCodeRangeExclusions(segmentCodeRanges: SingleSegmentMstRange[]) {
  return Object.fromEntries(
    segmentCodeRanges.map((curr) => [curr.key.toString(), curr.segmentMstCodeRange.isExcluded]),
  );
}

function createSegmentMstFromEntries(segments: SingleSegmentMst[]) {
  return Object.fromEntries(segments.map((curr) => [curr.key.toString(), curr.segmentMstCode]));
}

function createSingleSegmentMstFromEntries(segments: Record<string, string>) {
  return Object.entries(segments).map((curr) => {
    return { key: Number(curr[0]), segmentMstCode: curr[1] };
  });
}

function createSegmentCodes(segments: SingleSegmentMst[]) {
  return Object.fromEntries(segments.map((curr) => [curr.key.toString(), curr.segmentMstCode]));
}

function createSegmentCodeItem(
  segmentMstKey: number,
  metaSegments: SegmentMstSettingProvider[],
  required = false,
) {
  return {
    [`segmentMstCode-${segmentMstKey}`]: Item.of(
      createSegmentMstItemName(segmentMstKey, metaSegments),
      createSegmentMstItemAttribute(getMetaSegment(segmentMstKey, metaSegments), required),
    ),
  };
}

export {
  createHigherSegmentMstItem,
  createSegmentCodeItem,
  createSegmentCodeRangeBodies,
  createSegmentCodeRangeExclusions,
  createSegmentCodes,
  createSegmentMstCodeItem,
  createSegmentMstCodeRangeItem,
  createSegmentMstFromEntries,
  createSegmentMstItem,
  createSegmentMstItemAttribute,
  createSegmentMstItemName,
  createSegmentMstListSearchFieldConfig,
  createSegmentMstRegistrationFieldConfig,
  createSingleSegmentMstFromEntries,
  getHigherRankMetaSegment,
  getMetaSegment,
};
